import { Component, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TicketService } from '../common/services/ticket.service';
import { FormsModule, NgForm } from '@angular/forms';
import { forkJoin, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { FormCommonDataService } from 'src/sb-modules/Frontend-form-field-module/common/services/form-common-data.service';
import { FormService } from 'src/sb-modules/Frontend-form-field-module/common/services/form.service';
import { FormType } from '@sb-tools/typescript/enums/form-type.enum';
import { OutlookTicketModel } from '../common/models/outlook-ticket.model';
import { UserDataService } from '@sb-tools/typescript/services/user-data.service';
import { environment } from '@environment';
import { NewOutlookTicket } from '../common/models/new-outlook-ticket.model';
import { TicketIdWithCommunity } from '../common/models/ticket-id-with-community.model';
import { UserRole } from '@sb-tools/typescript/enums/user-role.enum';
import { SimpleCommunityWithOpenTicketsCount } from '@sb-tools/typescript/models/simple-community-with-open-tickets-count.model';
import { FormFieldsValidationService } from '@form-field-module/common/services/form-fields-validation.service';
import { FieldType } from '@sb-tools/typescript/enums/field-type.enum';
import { OutlookUserService } from '@form-field-module/common/services/outlook-user.service';
import Item = Office.Item;
import ItemCompose = Office.ItemCompose;
import ItemRead = Office.ItemRead;
import Message = Office.Message;
import MessageCompose = Office.MessageCompose;
import MessageRead = Office.MessageRead;
import Appointment = Office.Appointment;
import AppointmentCompose = Office.AppointmentCompose;
import AppointmentRead = Office.AppointmentRead;
import { OutlookAttachmentDetails } from '../common/models/outlook-attachment-details.model';
import { LoaderComponent } from '@sb-tools/typescript/components/loader/loader.component';
import { WelcomeComponent } from './welcome/welcome.component';
import { TranslateModule } from '@ngx-translate/core';
import { InlineSVGComponent } from '../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgClass } from '@angular/common';
import { CheckFormInitDirective } from '@form-field-module/common/directives/check-form-init.directive';
import { FormFieldComponent } from '@form-field-module/form-field/form-field.component';
import { FilterInlineAttachmentsPipe } from '../common/pipes/filter-inline-attachments.pipe';
import { CheckFieldRulesDirective } from '@form-field-module/common/directives/check-field-rules.directive';
import { AuthenticationResult, createNestablePublicClientApplication } from '@azure/msal-browser';
import { MicrosoftAttachmentService } from '../common/services/microsoft-attachment.service';
import { AttachmentService } from '@sb-tools/typescript/services/attachment.service';
import { FileModelExtended } from '@sb-tools/typescript/models/FileModelExtended.model';
import { Observable } from 'rxjs/internal/Observable';

@Component({
    selector: 'app-form',
    templateUrl: './form.component.html',
    styleUrls: ['./form.component.scss'],
    imports: [
        LoaderComponent,
        WelcomeComponent,
        TranslateModule,
        InlineSVGComponent,
        NgSelectModule,
        FormsModule,
        NgClass,
        CheckFormInitDirective,
        FormFieldComponent,
        FilterInlineAttachmentsPipe,
        CheckFieldRulesDirective,
    ],
})
export class FormComponent implements OnInit, OnDestroy {
    @ViewChild('ticketForm') ticketForm: NgForm;

    createdTicketsData: TicketIdWithCommunity[] = [];
    currentMailboxItem: Item &
        ItemCompose &
        ItemRead &
        Message &
        MessageCompose &
        MessageRead &
        Appointment &
        AppointmentCompose &
        AppointmentRead; // eslint-disable-line
    formModel: NewOutlookTicket;
    formTypes = FormType;
    isAttachmentLoadingError = false;
    isEmailSelected: boolean;
    isLoading = true;
    isLoadingUserAndFormData: boolean;
    isReadOnlyUser: boolean;
    isSaving: boolean;
    isTicketCreated: boolean;
    newTicketLink: string;
    userRoleEnum = UserRole;

    private attachmentsMap: Record<string, FileModelExtended> = {};
    private unsubscriber = new Subject<void>();

    constructor(
        public formCommonDataService: FormCommonDataService,
        public formService: FormService,
        public outlookUserService: OutlookUserService,
        public userDataService: UserDataService,
        private attachmentService: AttachmentService,
        private formFieldsValidationService: FormFieldsValidationService,
        private microsoftAttachmentService: MicrosoftAttachmentService,
        private ticketService: TicketService,
        private zone: NgZone,
    ) {
        this.formCommonDataService.formName = 'outlook';
    }

    ngOnInit(): void {
        this.initMailboxItemChangeListener();
    }

    backToForm(): void {
        this.initMailboxItemData(false);
        this.onSelectCommunity({
            communityName: this.formCommonDataService.community,
        } as SimpleCommunityWithOpenTicketsCount);
    }

    cancel(): void {
        this.isEmailSelected = false;
    }

    copyLink(): void {
        const el = document.createElement('textarea');
        el.value = this.newTicketLink;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
    }

    onSelectCommunity(value: SimpleCommunityWithOpenTicketsCount): void {
        this.formCommonDataService.community = value?.communityName || null;

        if (value) {
            this.loadUserAndFormData();
        }
    }

    processCreateTicket(): void {
        this.formService.isSubmitted = true;

        if (this.ticketForm.valid && this.formFieldsValidationService.isRequiredValuesFilled(this.formModel.fields)) {
            this.isSaving = true;

            const selectedAttachments = this.formModel.allAttachments.filter((attachment) => {
                return attachment.isMarked;
            });

            if (selectedAttachments && selectedAttachments.length && !this.isAttachmentLoadingError) {
                const formAttachments: FileModelExtended[] = [];
                selectedAttachments.forEach((attachment) => {
                    formAttachments.push(this.attachmentsMap[attachment.contentId]);
                });
                this.formModel.attachments = formAttachments;
                this.createTicket();
            } else {
                this.formModel.attachments = null;
                this.createTicket();
            }
        }
    }

    private createBlobFromBase64(contentBytes: string, sliceSize = 512): Blob {
        const byteCharacters = atob(contentBytes);
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);

            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }
        return new Blob(byteArrays);
    }

    private createTicket(): void {
        const ticketModel = new OutlookTicketModel(
            this.formModel,
            this.formFieldsValidationService.checkAndSetValidValues.bind(this.formFieldsValidationService),
        );

        this.ticketService
            .send(ticketModel, this.formModel.community)
            .pipe(
                takeUntil(this.unsubscriber),
                finalize(() => {
                    this.isSaving = false;
                }),
            )
            .subscribe((ticketId) => {
                this.newTicketLink = this.getTicketLink(this.formModel.community, ticketId);
                // zone is needed here because code is run out of angular zone (in office js)
                this.zone.run(() => {
                    this.isTicketCreated = true;
                });
            });
    }

    private initFormModel(): void {
        this.formModel = new NewOutlookTicket();

        if (this.outlookUserService.communities.length === 1) {
            this.formModel.community = this.outlookUserService.communities[0].communityName;
            this.onSelectCommunity(this.outlookUserService.communities[0]);
        }

        this.formService.isSubmitted = false;
    }

    private initMailboxItemChangeListener(): void {
        Office.onReady(() => {
            if (!Office.context.mailbox) {
                return;
            }

            Office.context.mailbox.addHandlerAsync(
                Office.EventType.ItemChanged,
                this.initMailboxItemData.bind(this),
                this.initMailboxItemData.bind(this),
            );
        });
    }

    private checkIfTicketWasCreated(): void {
        this.ticketService
            .getTicketsByItemId(this.currentMailboxItem.itemId)
            .pipe(
                takeUntil(this.unsubscriber),
                finalize(() => {
                    this.isLoading = false;
                }),
            )
            .subscribe((ticketsData) => {
                if (!ticketsData.length) {
                    this.isEmailSelected = true;
                    return;
                }

                ticketsData.forEach((ticket) => {
                    ticket.link = this.getTicketLink(ticket.communityName, ticket.ticketId);
                });
                this.createdTicketsData = ticketsData;
            });
    }

    private checkUserPermission(): void {
        this.isReadOnlyUser = this.outlookUserService.user?.role === this.userRoleEnum.readOnlyUser;
    }

    private getTicketLink(communityName: string, ticketId: number): string {
        return environment.inboxUrl + '/' + communityName + '/tickets?ticketId=' + ticketId;
    }

    private hideCommunitySelectorField(form): void {
        form.fields = form.fields.filter((field) => {
            return field.type !== FieldType.inbox;
        });
    }

    private async initMailboxItemData(isCheckTicketCreation = true): Promise<void> {
        this.isTicketCreated = false;
        this.formCommonDataService.isMessageContentLoaded = false;

        this.currentMailboxItem = Office.context.mailbox.item;

        if (this.currentMailboxItem && isCheckTicketCreation) {
            // zone is needed here because code is run out of angular zone (in office js)
            this.zone.run(() => {
                this.checkIfTicketWasCreated();
            });
        }

        this.initFormModel();

        const attachmentsList = this.currentMailboxItem.attachments as OutlookAttachmentDetails[];
        attachmentsList.forEach((attachment) => {
            if (!attachment.isInline) {
                attachment.isMarked = true;
            }
        });

        // zone.run is needed here because code is run out of angular zone (in office js)
        this.zone.run(() => {
            this.formModel.allAttachments = attachmentsList;
            this.formModel.subject = this.currentMailboxItem.subject;
            this.formModel.name = this.currentMailboxItem.sender.displayName;
            this.formModel.email = this.currentMailboxItem.sender.emailAddress;

            const ccEmails = this.currentMailboxItem.cc.map((item) => item.emailAddress);
            const toEmails = this.currentMailboxItem.to.map((item) => item.emailAddress);
            const sourceEmail = Office.context.mailbox.userProfile.emailAddress;

            this.formModel.sourceEmail = sourceEmail;
            this.formModel.cc = ccEmails.concat(toEmails).filter((email) => {
                return email.toLowerCase() !== sourceEmail.toLowerCase();
            });
            this.formModel.senderInfo = this.formModel.name + ' <' + this.formModel.email + '>';
        });

        this.currentMailboxItem.body.getAsync(Office.CoercionType.Html, (result) => {
            // zone.run is needed here because code is run out of angular zone (in office js)
            this.zone.run(() => {
                this.formModel.content = result.value ? result.value.replace(/(\r\n|\n|\r)/gm, '') : '';
                this.loadAttachmentsAndMessagePreview();
            });
        });

        this.formModel.itemId = this.currentMailboxItem.itemId;

        console.log('itemChanged', this.formModel, this.currentMailboxItem);
    }

    private async loadAttachmentsAndMessagePreview(): Promise<void> {
        const MSAL_CONFIG = {
            auth: {
                clientId: '89a3c175-5da6-408e-b5e7-d1d5f6ed8744',
                authority: 'https://login.microsoftonline.com/common',
            },
        };
        const msalInstance = await createNestablePublicClientApplication(MSAL_CONFIG);
        const tokenRequest = { scopes: ['Mail.Read'] };
        let loginResponse: AuthenticationResult;
        try {
            loginResponse = await msalInstance.acquireTokenSilent(tokenRequest);
        } catch (error) {
            try {
                loginResponse = await msalInstance.acquireTokenPopup(tokenRequest);
            } catch (popupError) {
                this.formCommonDataService.isMessageContentLoaded = true;
                this.isAttachmentLoadingError = true;
                return;
            }
        }

        const restId = Office.context.mailbox.convertToRestId(
            this.currentMailboxItem.itemId,
            Office.MailboxEnums.RestVersion.v2_0,
        );

        if (this.formModel.allAttachments.length) {
            this.microsoftAttachmentService
                .getAttachments(loginResponse.accessToken, restId)
                .pipe(takeUntil(this.unsubscriber))
                .subscribe((msData) => {
                    // zone.run is needed here because code is run out of angular zone (in office js)
                    this.zone.run(() => {
                        this.uploadAttachments(msData);
                    });
                });
        } else {
            this.formCommonDataService.isMessageContentLoaded = true;
        }
    }

    private uploadAttachments(msData: any): void {
        const uploads$: Observable<FileModelExtended>[] = msData.value.map((attachment) => {
            const blob = this.createBlobFromBase64(attachment.contentBytes);
            const sendingFile = new File([blob], attachment.name);

            return this.attachmentService.uploadFile(sendingFile);
        });

        forkJoin(uploads$)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((files) => {
                files.forEach((file, index) => {
                    const cid = msData.value[index].contentId;
                    this.attachmentsMap[cid] = file;
                });

                const hasAttachmentsWithCidSource = this.formModel.content.includes('src="cid:');
                if (hasAttachmentsWithCidSource) {
                    this.formModel.content = this.replaceCidImages(this.formModel.content, this.attachmentsMap);
                    this.processFormFieldsValues();
                }
                this.formCommonDataService.isMessageContentLoaded = true;
            });
    }

    private loadUserAndFormData(): void {
        this.isLoadingUserAndFormData = true;

        forkJoin({
            userInfo: this.userDataService.getShortUserInfo(),
            form: this.formService.get('outlook', this.formCommonDataService.language),
        })
            .pipe(takeUntil(this.unsubscriber))
            .subscribe(({ userInfo, form }) => {
                this.outlookUserService.user = userInfo;
                this.checkUserPermission();
                this.hideCommunitySelectorField(form);
                this.formCommonDataService.form = form;
                this.formModel.fields = form.fields;
                this.processFormFieldsValues();
                this.isLoadingUserAndFormData = false;
            });
    }

    private processFormFieldsValues(): void {
        const messageField = this.formModel.fields.find((field) => {
            return field.type === FieldType.message;
        });

        if (messageField) {
            messageField.value = this.formModel.content;
        }

        const creatorInfoField = this.formModel.fields.find((field) => {
            return field.type === FieldType.userIdentification;
        });

        if (creatorInfoField) {
            creatorInfoField.valueModel = this.formModel.senderInfo;
            creatorInfoField.value = this.formModel.email;
        }

        const titleField = this.formModel.fields.find((field) => {
            return field.type === FieldType.title;
        });

        if (titleField) {
            titleField.value = this.formModel.subject;
        }
    }

    private replaceCidImages(html: string, cidMap: Record<string, FileModelExtended>): string {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const images = doc.querySelectorAll('img');

        images.forEach((img) => {
            const src = img.getAttribute('src');

            if (src?.startsWith('cid:')) {
                const cid = src.substring(4);
                const newUrl = cidMap[cid].url;
                img.setAttribute('src', newUrl);
            }
        });
        return doc.body.innerHTML;
    }

    ngOnDestroy(): void {
        this.unsubscriber.next();
        this.unsubscriber.complete();

        if (Office.context.mailbox) {
            Office.context.mailbox.removeHandlerAsync(Office.EventType.ItemChanged);
        }
    }
}
